import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import GaDashboardView from './GaDashboardView';
import GaDashboardHelpModal from './GaDashboardHelpModal';
import { makeGaBlobFetcher, prefetchDashboardDrawing } from './gaDrawingCache';
import PressProgressOnePager from './PressProgressOnePager';
import AreasOfConcernOnePager from './AreasOfConcernOnePager';
import GaDashboardCard from './GaDashboardCard';
import GaCompanionCard from './GaCompanionCard';
import {
  groupDashboards, loadCollapsed, saveCollapsed,
  companionId, isCompanionId, companionKeyFromId,
} from './gaDashboardOrganize';

const fmt = (d) => (d ? String(d).slice(0, 10) : '—');

const orderKey = (jobNumber) => `ga-dashboard-order-${jobNumber}`;

/** Apply a saved personal order (localStorage) — unknown/new ids append at the end. */
const applyPersonalOrder = (list, jobNumber) => {
  if (!jobNumber || !list?.length) return list || [];
  let saved = [];
  try { saved = JSON.parse(localStorage.getItem(orderKey(jobNumber)) || '[]'); } catch { saved = []; }
  if (!Array.isArray(saved) || !saved.length) return list;
  // IDs may be number or string depending on how they were JSON.stringified
  const byId = new Map(list.map((d) => [Number(d.id), d]));
  const ordered = [];
  const seen = new Set();
  for (const raw of saved) {
    const id = Number(raw);
    if (byId.has(id) && !seen.has(id)) {
      ordered.push(byId.get(id));
      seen.add(id);
    }
  }
  for (const d of list) {
    const id = Number(d.id);
    if (!seen.has(id)) ordered.push(d);
  }
  return ordered;
};

const savePersonalOrder = (list, jobNumber) => {
  if (!jobNumber) return;
  try { localStorage.setItem(orderKey(jobNumber), JSON.stringify(list.map((d) => Number(d.id)))); } catch { /* private mode */ }
};

const isSectionCollapsed = (collapsed, key) => {
  if (collapsed && Object.prototype.hasOwnProperty.call(collapsed, key)) return Boolean(collapsed[key]);
  return key === 'archive';
};

/**
 * GA Dashboards (Schedule module entry).
 *
 * Visual schedule dashboards: P6 dates overlaid on General Arrangement
 * drawings. Schedule updates come in as snapshots — imported manually
 * (XER/XLSX) or picked up automatically from the project's SharePoint
 * schedule folder every weekday morning.
 */
const GaDashboards = ({ jobNumber, jobName, projectId }) => {
  const { instance, accounts } = useMsal();
  const [dashboards, setDashboards] = useState([]);
  const [folders, setFolders] = useState([]);
  const [collapsed, setCollapsed] = useState({ archive: true });
  const [snapshots, setSnapshots] = useState([]);
  const [syncConfig, setSyncConfig] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [showCreate, setShowCreate] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardDash, setWizardDash] = useState(null);
  const [showCopy, setShowCopy] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [catalogQ, setCatalogQ] = useState('71940');
  const [copying, setCopying] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const wizardFileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderId, setNewFolderId] = useState('');
  const [renamingFolderId, setRenamingFolderId] = useState(null);
  const [renameFolderValue, setRenameFolderValue] = useState('');
  const [showPressProgress, setShowPressProgress] = useState(false);
  const [pressProgressAvailable, setPressProgressAvailable] = useState(false);
  const [showAreasOfConcern, setShowAreasOfConcern] = useState(false);
  const [areasOfConcernAvailable, setAreasOfConcernAvailable] = useState(false);
  const [specialCards, setSpecialCards] = useState([]);
  const importInputRef = useRef(null);

  const fetchAll = useCallback(async () => {
    if (!jobNumber) return;
    try {
      setLoading(true);
      setError(null);
      // Companions are always shown — do not probe Press Progress / AOC here.
      // Those GETs hit live P6 + SharePoint and used to hold the whole card grid.
      const isPressProgressJob = String(jobNumber) === '75998' || String(jobNumber) === 'XXXXX';
      const [dashRes, snapRes, syncRes, folderRes, specialRes] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', `/ga-dashboards/${jobNumber}/dashboards`),
        authenticatedApiCall(instance, accounts, 'GET', `/ga-dashboards/${jobNumber}/snapshots`),
        authenticatedApiCall(instance, accounts, 'GET', `/ga-dashboards/${jobNumber}/sync-config`),
        authenticatedApiCall(instance, accounts, 'GET', `/ga-dashboards/${jobNumber}/folders`).catch(() => ({ data: { data: [] } })),
        authenticatedApiCall(instance, accounts, 'GET', `/ga-dashboards/${jobNumber}/special-cards`).catch(() => ({ data: { data: [] } })),
      ]);
      setDashboards(applyPersonalOrder(dashRes.data.data || [], jobNumber));
      setFolders(folderRes.data.data || []);
      setSpecialCards(specialRes.data.data || []);
      setCollapsed(loadCollapsed(jobNumber));
      setSnapshots(snapRes.data.data || []);
      setSyncConfig(syncRes.data.data);
      setPressProgressAvailable(isPressProgressJob);
      setAreasOfConcernAvailable(true);
    } catch (e) {
      console.error('Failed to load GA dashboards:', e);
      setError(e.response?.data?.error || 'Failed to load GA dashboards');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, jobNumber]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchBlob = useCallback(
    makeGaBlobFetcher(authenticatedApiCall, instance, accounts),
    [instance, accounts]
  );

  const openDashboard = (d) => {
    if (d?.drawing_path) {
      prefetchDashboardDrawing(fetchBlob, d).catch(() => {});
    }
    setSelected(d);
  };

  const createDashboard = async ({ openAfter = true, forWizard = false } = {}) => {
    if (!newName.trim()) return null;
    try {
      setCreating(true);
      const res = await authenticatedApiCall(
        instance, accounts, 'POST',
        `/ga-dashboards/${jobNumber}/dashboards`,
        { name: newName.trim(), description: newDesc.trim() || undefined, folder_id: newFolderId || null }
      );
      const dash = res.data.data;
      setShowCreate(false);
      setNewName('');
      setNewDesc('');
      setNewFolderId('');
      await fetchAll();
      if (forWizard) {
        setWizardDash(dash);
        setWizardStep(2);
      } else if (openAfter) {
        setSelected(dash);
      }
      return dash;
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to create dashboard');
      return null;
    } finally {
      setCreating(false);
    }
  };

  const openWizard = () => {
    setShowWizard(true);
    setWizardStep(1);
    setWizardDash(null);
    setNewName('');
    setNewDesc('');
  };

  const wizardUploadDrawing = async (file) => {
    if (!wizardDash?.id || !file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      await authenticatedApiCall(
        instance, accounts, 'POST',
        `/ga-dashboards/dashboards/${wizardDash.id}/drawing`,
        formData, { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      await fetchAll();
      setWizardStep(3);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to upload drawing');
    }
  };

  const finishWizard = () => {
    const dash = wizardDash;
    setShowWizard(false);
    setWizardDash(null);
    setWizardStep(1);
    if (dash) setSelected(dash);
  };

  const loadCatalog = async (q = catalogQ) => {
    try {
      const res = await authenticatedApiCall(
        instance, accounts, 'GET',
        `/ga-dashboards/catalog?q=${encodeURIComponent(q || '')}`
      );
      setCatalog(res.data.data || []);
    } catch (e) {
      console.error('Failed to load GA catalog:', e);
      setCatalog([]);
    }
  };

  const copyFrom = async (sourceId, copyDrawing = true) => {
    try {
      setCopying(true);
      const res = await authenticatedApiCall(
        instance, accounts, 'POST',
        `/ga-dashboards/${jobNumber}/dashboards/copy-from`,
        { sourceId, copyDrawing }
      );
      setShowCopy(false);
      setNotice(`Copied layout from dashboard #${sourceId} — re-bind markers if activity codes differ on this job.`);
      await fetchAll();
      setSelected(res.data.data);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to copy dashboard');
    } finally {
      setCopying(false);
    }
  };

  const deleteDashboard = async (dash) => {
    if (!window.confirm(`Permanently delete "${dash.name}"? This cannot be undone from the list. Prefer Archive if you might need it again.`)) return;
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `/ga-dashboards/dashboards/${dash.id}`);
      await fetchAll();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to delete dashboard');
    }
  };

  const archiveDashboard = async (dash) => {
    try {
      if (dash.isSpecial || dash.specialKey) {
        await upsertSpecialCard(dash.specialKey, { archived: true });
        return;
      }
      await authenticatedApiCall(instance, accounts, 'POST', `/ga-dashboards/dashboards/${dash.id}/archive`);
      setDashboards((prev) => prev.map((d) => (
        Number(d.id) === Number(dash.id) ? { ...d, archived_at: new Date().toISOString() } : d
      )));
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to archive dashboard');
    }
  };

  const restoreDashboard = async (dash) => {
    try {
      if (dash.isSpecial || dash.specialKey) {
        await upsertSpecialCard(dash.specialKey, { archived: false });
        return;
      }
      await authenticatedApiCall(instance, accounts, 'POST', `/ga-dashboards/dashboards/${dash.id}/unarchive`);
      setDashboards((prev) => prev.map((d) => (
        Number(d.id) === Number(dash.id) ? { ...d, archived_at: null, archived_by: null } : d
      )));
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to restore dashboard');
    }
  };

  const moveToFolder = async (dash, folderId) => {
    try {
      if (dash.isSpecial || dash.specialKey) {
        await upsertSpecialCard(dash.specialKey, { folder_id: folderId || null, archived: false });
        return;
      }
      await authenticatedApiCall(instance, accounts, 'PATCH', `/ga-dashboards/dashboards/${dash.id}`, {
        folder_id: folderId || null,
      });
      setDashboards((prev) => prev.map((d) => (
        Number(d.id) === Number(dash.id) ? { ...d, folder_id: folderId || null } : d
      )));
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to move dashboard');
    }
  };

  const upsertSpecialCard = async (cardKey, body) => {
    const res = await authenticatedApiCall(
      instance, accounts, 'PUT',
      `/ga-dashboards/${jobNumber}/special-cards/${cardKey}`,
      body
    );
    const row = res.data.data;
    setSpecialCards((prev) => {
      const rest = prev.filter((s) => s.card_key !== cardKey);
      return [...rest, {
        card_key: cardKey,
        folder_id: row.folder_id,
        archived_at: row.archived_at,
        archived_by: row.archived_by,
      }];
    });
  };

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      await authenticatedApiCall(instance, accounts, 'POST', `/ga-dashboards/${jobNumber}/folders`, { name });
      setNewFolderName('');
      setShowNewFolder(false);
      await fetchAll();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to create folder');
    }
  };

  const renameFolder = async (folder) => {
    const name = renameFolderValue.trim();
    if (!name || name === folder.name) {
      setRenamingFolderId(null);
      return;
    }
    try {
      await authenticatedApiCall(instance, accounts, 'PATCH', `/ga-dashboards/folders/${folder.id}`, { name });
      setFolders((prev) => prev.map((f) => (Number(f.id) === Number(folder.id) ? { ...f, name } : f)));
      setRenamingFolderId(null);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to rename folder');
    }
  };

  const deleteFolder = async (folder) => {
    if (!window.confirm(`Remove folder "${folder.name}"? Boards inside it stay on the job, unfiled.`)) return;
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `/ga-dashboards/folders/${folder.id}`);
      await fetchAll();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to remove folder');
    }
  };

  const toggleCollapsed = (key) => {
    setCollapsed((prev) => {
      const next = { ...prev, [key]: !isSectionCollapsed(prev, key) };
      saveCollapsed(jobNumber, next);
      return next;
    });
  };

  const dropOnFolder = async (folderKey, fromId) => {
    const from = findCard(fromId);
    if (!from) return;
    if (folderKey === 'archive') {
      if (!from.archived_at) await archiveDashboard(from);
      return;
    }
    if (from.archived_at) await restoreDashboard(from);
    const targetFolder = folderKey === 'unfiled' ? null : Number(folderKey);
    const current = from.folder_id == null ? null : Number(from.folder_id);
    if (current !== targetFolder) await moveToFolder(from, targetFolder);
  };

  const dropOnCard = async (fromId, toDash) => {
    const from = findCard(fromId);
    if (!from) return;
    const fromKey = from.specialKey ? companionId(from.specialKey) : Number(from.id);
    const toKey = toDash.specialKey ? companionId(toDash.specialKey) : Number(toDash.id);
    if (String(fromKey) === String(toKey)) return;
    const fromArch = Boolean(from.archived_at);
    const toArch = Boolean(toDash.archived_at);
    if (fromArch !== toArch) {
      if (toArch) await archiveDashboard(from);
      else await restoreDashboard(from);
    }
    if (!toArch) {
      const fromFolder = from.folder_id == null ? null : Number(from.folder_id);
      const toFolder = toDash.folder_id == null ? null : Number(toDash.folder_id);
      if (fromFolder !== toFolder) await moveToFolder(from, toFolder);
    }
    if (!from.specialKey && !toDash.specialKey) reorderDashboards(fromId, toDash.id);
  };

  const reorderDashboards = (fromId, toId) => {
    if (!fromId || !toId || Number(fromId) === Number(toId)) return;
    setDashboards((prev) => {
      const next = [...prev];
      const fromIdx = next.findIndex((d) => Number(d.id) === Number(fromId));
      const toIdx = next.findIndex((d) => Number(d.id) === Number(toId));
      if (fromIdx < 0 || toIdx < 0) return prev;
      const [item] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, item);
      savePersonalOrder(next, jobNumber);
      return next;
    });
  };

  const resetPersonalOrder = () => {
    try { localStorage.removeItem(orderKey(jobNumber)); } catch { /* ignore */ }
    fetchAll();
  };

  const importSchedule = async (file) => {
    try {
      setImporting(true);
      setNotice(null);
      const formData = new FormData();
      formData.append('file', file);
      const res = await authenticatedApiCall(
        instance, accounts, 'POST',
        `/ga-dashboards/${jobNumber}/import`,
        formData, { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const snap = res.data.data;
      setNotice(`Imported ${snap.file_name}: ${snap.activity_count} activities (data date ${fmt(snap.data_date)})`);
      await fetchAll();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to import schedule file');
    } finally {
      setImporting(false);
    }
  };

  const syncNow = async () => {
    try {
      setSyncing(true);
      setNotice(null);
      const res = await authenticatedApiCall(instance, accounts, 'POST', `/ga-dashboards/${jobNumber}/sync-now`);
      const r = res.data.data;
      setNotice(r.imported?.length
        ? `SharePoint sync: imported ${r.imported.length} new update(s)`
        : `SharePoint sync: no new schedule files (${r.filesFound} checked)`);
      await fetchAll();
    } catch (e) {
      alert(e.response?.data?.error || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const companionItems = [
    pressProgressAvailable && {
      specialKey: 'press_progress',
      name: 'Press Progress',
      folder_id: specialCards.find((s) => s.card_key === 'press_progress')?.folder_id ?? null,
      archived_at: specialCards.find((s) => s.card_key === 'press_progress')?.archived_at ?? null,
    },
    areasOfConcernAvailable && {
      specialKey: 'areas_of_concern',
      name: 'Areas of Concern',
      folder_id: specialCards.find((s) => s.card_key === 'areas_of_concern')?.folder_id ?? null,
      archived_at: specialCards.find((s) => s.card_key === 'areas_of_concern')?.archived_at ?? null,
    },
  ].filter(Boolean);

  const grouped = groupDashboards(dashboards, folders, companionItems);
  const liveCount = dashboards.filter((d) => !d.archived_at).length
    + companionItems.filter((c) => !c.archived_at).length;

  const findCard = (id) => {
    if (isCompanionId(id)) {
      const key = companionKeyFromId(id);
      return companionItems.find((c) => c.specialKey === key) || null;
    }
    return dashboards.find((d) => Number(d.id) === Number(id)) || null;
  };

  const cardGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: 14,
    width: '100%',
  };

  const renderCard = (d, archived = false) => {
    const cardId = d.specialKey ? companionId(d.specialKey) : d.id;
    const dragHandlers = {
      onDragStart: (e) => {
        setDragId(cardId);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(cardId));
      },
      onDragEnd: () => { setDragId(null); setDragOverId(null); setDragOverFolder(null); },
      onDragOver: (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverId(cardId); },
      onDragLeave: () => { if (String(dragOverId) === String(cardId)) setDragOverId(null); },
      onDrop: (e) => {
        e.preventDefault();
        e.stopPropagation();
        const from = e.dataTransfer.getData('text/plain') || dragId;
        dropOnCard(from, d);
        setDragId(null);
        setDragOverId(null);
        setDragOverFolder(null);
      },
    };
    if (d.specialKey) {
      return (
        <GaCompanionCard
          key={cardId}
          cardKey={d.specialKey}
          folders={folders}
          archived={archived}
          isDragging={String(dragId) === String(cardId)}
          isOver={String(dragOverId) === String(cardId) && String(dragId) !== String(cardId)}
          onOpen={() => {
            if (d.specialKey === 'press_progress') setShowPressProgress(true);
            else setShowAreasOfConcern(true);
          }}
          onArchive={() => archiveDashboard(d)}
          onRestore={() => restoreDashboard(d)}
          onMoveToFolder={(folderId) => moveToFolder(d, folderId)}
          {...dragHandlers}
        />
      );
    }
    return (
      <GaDashboardCard
        key={d.id}
        dashboard={d}
        folders={folders}
        archived={archived}
        isDragging={Number(dragId) === Number(d.id)}
        isOver={Number(dragOverId) === Number(d.id) && Number(dragId) !== Number(d.id)}
        onOpen={openDashboard}
        onArchive={archiveDashboard}
        onRestore={restoreDashboard}
        onDelete={deleteDashboard}
        onMoveToFolder={moveToFolder}
        {...dragHandlers}
      />
    );
  };

  const renderSectionHeader = (key, title, count, extra = null) => {
    const open = !isSectionCollapsed(collapsed, key);
    const over = String(dragOverFolder) === String(key);
    return (
      <div
        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverFolder(key); }}
        onDragLeave={() => { if (String(dragOverFolder) === String(key)) setDragOverFolder(null); }}
        onDrop={(e) => {
          e.preventDefault();
          const from = e.dataTransfer.getData('text/plain') || dragId;
          dropOnFolder(key, from);
          setDragId(null);
          setDragOverId(null);
          setDragOverFolder(null);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 10px',
          borderRadius: 8,
          margin: '4px 0 10px',
          background: over ? '#ecfdf5' : '#f8fafc',
          border: over ? '1px dashed #0f766e' : '1px solid #e2e8f0',
        }}
      >
        <button
          type="button"
          onClick={() => toggleCollapsed(key)}
          style={{ border: 'none', background: 'none', cursor: 'pointer', fontWeight: 700, color: '#0f172a', fontSize: 14, padding: 0 }}
        >
          {open ? '▾' : '▸'} {title}
        </button>
        <span style={{ fontSize: 12, color: '#94a3b8' }}>{count}</span>
        <div style={{ flex: 1 }} />
        {extra}
      </div>
    );
  };

  if (showPressProgress) {
    return (
      <PressProgressOnePager
        jobNumber={jobNumber}
        onBack={() => setShowPressProgress(false)}
      />
    );
  }

  if (showAreasOfConcern) {
    return (
      <AreasOfConcernOnePager
        jobNumber={jobNumber}
        jobName={jobName}
        onBack={() => setShowAreasOfConcern(false)}
      />
    );
  }

  if (selected) {
    return (
      <>
        <GaDashboardView
          jobNumber={jobNumber}
          jobName={jobName}
          projectId={projectId}
          dashboard={selected}
          snapshots={snapshots}
          onBack={() => { setSelected(null); fetchAll(); }}
          onDashboardChanged={fetchAll}
          onOpenHelp={() => setShowHelp(true)}
        />
        <GaDashboardHelpModal open={showHelp} onClose={() => setShowHelp(false)} />
      </>
    );
  }

  const latest = snapshots[0];

  return (
    <div style={{ padding: '1rem 1.5rem', width: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <h2 style={{ margin: 0, fontSize: 22 }}>🗺 GA Dashboards</h2>
        <button
          type="button"
          onClick={() => setShowHelp(true)}
          title="How to use GA Dashboards"
          style={{
            padding: '6px 12px', borderRadius: 8, border: '1px solid #99f6e4', background: '#f0fdfa',
            color: '#0f766e', fontWeight: 700, cursor: 'pointer', fontSize: 13,
          }}
        >
          ❓ Help
        </button>
        <div style={{ flex: 1 }} />
        {dashboards.filter((d) => !d.archived_at).length > 1 && (
          <button
            onClick={resetPersonalOrder}
            title="Reset your personal card order to the default"
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontSize: 12, color: '#475569' }}
          >
            ↺ Reset order
          </button>
        )}
        <button
          onClick={() => { setShowNewFolder(true); setNewFolderName(''); }}
          style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', color: '#0f766e', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}
        >
          + Folder
        </button>
        <button
          onClick={() => { setShowCopy(true); loadCatalog(catalogQ); }}
          style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', color: '#0f766e', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}
        >
          ⧉ Copy layout…
        </button>
        <button
          onClick={openWizard}
          style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #99f6e4', background: '#f0fdfa', color: '#0f766e', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}
        >
          🪄 Setup wizard
        </button>
        <button
          onClick={() => setShowCreate(true)}
          style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#0f766e', color: '#fff', fontWeight: 700, cursor: 'pointer' }}
        >
          + New dashboard
        </button>
      </div>
      <p style={{ color: '#64748b', marginTop: 4, fontSize: 13 }}>
        Live P6 dates and labor units on your General Arrangement drawings — pick a Rev, bind regions to WBS or activities, and every update refreshes the board.
        {dashboards.filter((d) => !d.archived_at).length > 1 && <> Drag a card onto a folder (or another card) to file it. Archive hides a board without deleting it.</>}
      </p>

      {error && <div style={{ padding: 12, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, color: '#dc2626', marginBottom: 12 }}>{error}</div>}
      {notice && <div style={{ padding: 12, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, color: '#166534', marginBottom: 12 }}>{notice}</div>}

      {/* Schedule updates panel */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 18px', marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 14 }}>📅 Schedule updates</strong>
          <span style={{ fontSize: 13, color: '#475569' }}>
            {latest
              ? <>Latest: <strong>{latest.file_name}</strong> · data date {fmt(latest.data_date)} · {latest.activity_count} activities · {snapshots.length} update{snapshots.length > 1 ? 's' : ''} on file</>
              : 'No schedule imported yet'}
          </span>
          <div style={{ flex: 1 }} />
          <button
            onClick={() => importInputRef.current?.click()}
            disabled={importing}
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontSize: 13 }}
          >
            {importing ? 'Importing…' : '⬆ Import XER / Excel'}
          </button>
          <button
            onClick={syncNow}
            disabled={syncing || !syncConfig}
            title={syncConfig ? `Checks ${syncConfig.folder_path} on ${syncConfig.site_path}` : 'No SharePoint sync configured for this job'}
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontSize: 13, opacity: syncConfig ? 1 : 0.5 }}
          >
            {syncing ? 'Syncing…' : '☁ Sync from SharePoint'}
          </button>
          <input
            ref={importInputRef} type="file" accept=".xer,.xlsx,.xls" style={{ display: 'none' }}
            onChange={(e) => { if (e.target.files?.[0]) importSchedule(e.target.files[0]); e.target.value = ''; }}
          />
        </div>
        {syncConfig && (
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
            Auto-sync {syncConfig.enabled ? 'ON' : 'OFF'} · watches "{syncConfig.folder_path}" every weekday 6 AM
            {syncConfig.last_checked_at ? ` · last check ${fmt(syncConfig.last_checked_at)}` : ''}
          </div>
        )}
      </div>

      {/* Dashboard cards */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading…</div>
      ) : !liveCount && !grouped.archived.length && !pressProgressAvailable && !areasOfConcernAvailable ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#475569', background: '#fff', border: '2px dashed #99f6e4', borderRadius: 12 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🗺</div>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a', marginBottom: 6 }}>No dashboards yet</div>
          <p style={{ maxWidth: 420, margin: '0 auto 16px', fontSize: 13, lineHeight: 1.45 }}>
            Three steps: name a board, drop your GA drawing, then pin a region and bind it to a WBS or activity. Or copy a layout from another job (e.g. 71940).
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={openWizard} style={{ padding: '10px 18px', borderRadius: 8, border: 'none', background: '#0f766e', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
              🪄 Start setup wizard
            </button>
            <button onClick={() => { setShowCopy(true); loadCatalog('71940'); }} style={{ padding: '10px 18px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', color: '#0f766e', fontWeight: 700, cursor: 'pointer' }}>
              ⧉ Copy from 71940…
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {grouped.folderGroups.map(({ folder, dashboards: inFolder }) => {
            const key = String(folder.id);
            const open = !isSectionCollapsed(collapsed, key);
            return (
              <div key={folder.id}>
                {renderSectionHeader(
                  key,
                  folder.name,
                  inFolder.length,
                  <span style={{ display: 'flex', gap: 6 }}>
                    {renamingFolderId === folder.id ? (
                      <input
                        autoFocus
                        value={renameFolderValue}
                        onChange={(e) => setRenameFolderValue(e.target.value)}
                        onBlur={() => renameFolder(folder)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') renameFolder(folder);
                          if (e.key === 'Escape') setRenamingFolderId(null);
                        }}
                        style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13, width: 160 }}
                      />
                    ) : (
                      <button
                        type="button"
                        title="Rename folder"
                        onClick={() => { setRenamingFolderId(folder.id); setRenameFolderValue(folder.name); }}
                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b', fontSize: 12 }}
                      >
                        Rename
                      </button>
                    )}
                    <button
                      type="button"
                      title="Remove folder (boards stay)"
                      onClick={() => deleteFolder(folder)}
                      style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 12 }}
                    >
                      Remove
                    </button>
                  </span>
                )}
                {open && (
                  inFolder.length
                    ? <div style={cardGridStyle}>{inFolder.map((d) => renderCard(d, false))}</div>
                    : <div style={{ fontSize: 12, color: '#94a3b8', padding: '0 10px 8px' }}>Drop a board here</div>
                )}
              </div>
            );
          })}

          {(folders.length > 0 || grouped.unfiled.length > 0) && (
            <div>
              {folders.length > 0 && renderSectionHeader('unfiled', 'Unfiled', grouped.unfiled.length)}
              {(folders.length === 0 || !isSectionCollapsed(collapsed, 'unfiled')) && grouped.unfiled.length > 0 && (
                <div style={cardGridStyle}>{grouped.unfiled.map((d) => renderCard(d, false))}</div>
              )}
              {folders.length > 0 && !isSectionCollapsed(collapsed, 'unfiled') && !grouped.unfiled.length && (
                <div style={{ fontSize: 12, color: '#94a3b8', padding: '0 10px 8px' }}>Drop a board here to take it out of a folder</div>
              )}
            </div>
          )}

          {(liveCount > 0 || grouped.archived.length > 0) && (
            <div>
              {renderSectionHeader('archive', 'Archived', grouped.archived.length)}
              {!isSectionCollapsed(collapsed, 'archive') && (
                grouped.archived.length
                  ? <div style={cardGridStyle}>{grouped.archived.map((d) => renderCard(d, true))}</div>
                  : <div style={{ fontSize: 12, color: '#94a3b8', padding: '0 10px 8px' }}>Drop a board here to archive it — drawings and pins stay</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 440, maxWidth: '92vw', overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', background: '#0f766e', color: '#fff', display: 'flex', justifyContent: 'space-between' }}>
              <strong>New GA dashboard</strong>
              <button onClick={() => setShowCreate(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input
                value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus
                placeholder='Name, e.g. "Overall Site Plan" or "GT Exhaust Area"'
                style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                onKeyDown={(e) => { if (e.key === 'Enter') createDashboard(); }}
              />
              <textarea
                value={newDesc} onChange={(e) => setNewDesc(e.target.value)}
                placeholder="Description (optional)" rows={2}
                style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, resize: 'vertical' }}
              />
              {folders.length > 0 && (
                <select
                  value={newFolderId}
                  onChange={(e) => setNewFolderId(e.target.value)}
                  style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                >
                  <option value="">Unfiled</option>
                  {folders.map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              )}
              <button
                onClick={() => createDashboard()} disabled={creating || !newName.trim()}
                style={{ padding: '10px 16px', borderRadius: 8, border: 'none', background: '#0f766e', color: '#fff', fontWeight: 700, cursor: 'pointer', opacity: !newName.trim() ? 0.5 : 1 }}
              >
                {creating ? 'Creating…' : 'Create dashboard'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNewFolder && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 400, maxWidth: '92vw', overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', background: '#0f766e', color: '#fff', display: 'flex', justifyContent: 'space-between' }}>
              <strong>New folder</strong>
              <button onClick={() => setShowNewFolder(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input
                value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} autoFocus
                placeholder='Name, e.g. "Presentations" or "Sequences"'
                style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                onKeyDown={(e) => { if (e.key === 'Enter') createFolder(); }}
              />
              <button
                onClick={createFolder} disabled={!newFolderName.trim()}
                style={{ padding: '10px 16px', borderRadius: 8, border: 'none', background: '#0f766e', color: '#fff', fontWeight: 700, cursor: 'pointer', opacity: !newFolderName.trim() ? 0.5 : 1 }}
              >
                Create folder
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Setup wizard */}
      {showWizard && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 520, maxWidth: '94vw', overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', background: '#0f766e', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong>Setup wizard — step {wizardStep} of 3</strong>
              <button onClick={() => setShowWizard(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13, color: '#334155' }}>
              <div style={{ display: 'flex', gap: 6 }}>
                {[1, 2, 3].map((s) => (
                  <div key={s} style={{ flex: 1, height: 4, borderRadius: 2, background: wizardStep >= s ? '#0f766e' : '#e2e8f0' }} />
                ))}
              </div>
              {wizardStep === 1 && (
                <>
                  <p style={{ margin: 0 }}><strong>1. Name the board</strong> — e.g. Overall Site Plan or Stack Field Assembly.</p>
                  <input
                    value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus
                    placeholder="Dashboard name"
                    style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && newName.trim()) createDashboard({ forWizard: true, openAfter: false }); }}
                  />
                  <button
                    onClick={() => createDashboard({ forWizard: true, openAfter: false })}
                    disabled={creating || !newName.trim()}
                    style={{ padding: '10px 16px', borderRadius: 8, border: 'none', background: '#0f766e', color: '#fff', fontWeight: 700, cursor: 'pointer', opacity: !newName.trim() ? 0.5 : 1 }}
                  >
                    {creating ? 'Creating…' : 'Next — add drawing'}
                  </button>
                </>
              )}
              {wizardStep === 2 && (
                <>
                  <p style={{ margin: 0 }}><strong>2. Add the GA drawing</strong> — PDF or image of the arrangement.</p>
                  <button
                    onClick={() => wizardFileRef.current?.click()}
                    style={{ padding: '12px 16px', borderRadius: 8, border: '2px dashed #99f6e4', background: '#f0fdfa', color: '#0f766e', fontWeight: 700, cursor: 'pointer' }}
                  >
                    ⬆ Upload PDF / image
                  </button>
                  <input
                    ref={wizardFileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" style={{ display: 'none' }}
                    onChange={(e) => { if (e.target.files?.[0]) wizardUploadDrawing(e.target.files[0]); e.target.value = ''; }}
                  />
                  <button onClick={() => setWizardStep(3)} style={{ border: 'none', background: 'none', color: '#64748b', cursor: 'pointer', fontSize: 12 }}>
                    Skip for now
                  </button>
                </>
              )}
              {wizardStep === 3 && (
                <>
                  <p style={{ margin: 0 }}><strong>3. Pin and bind</strong> — open the board, click Edit, drop a pin or zone, then Bind to a WBS or activity. Pick a Rev in the toolbar to pull live P6 dates and units. A multi-page PDF becomes a scrollable sequence; each page can have its own pin.</p>
                  <button
                    onClick={finishWizard}
                    style={{ padding: '10px 16px', borderRadius: 8, border: 'none', background: '#0f766e', color: '#fff', fontWeight: 700, cursor: 'pointer' }}
                  >
                    Open dashboard and edit
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Copy layout modal */}
      {showCopy && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 560, maxWidth: '94vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', background: '#0f766e', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong>Copy layout from another job</strong>
              <button onClick={() => setShowCopy(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 14, borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 8 }}>
              <input
                value={catalogQ}
                onChange={(e) => setCatalogQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') loadCatalog(catalogQ); }}
                placeholder="Search job or name…"
                style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
              />
              <button onClick={() => loadCatalog(catalogQ)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontWeight: 600 }}>Search</button>
            </div>
            <div style={{ padding: 12, overflow: 'auto', flex: 1 }}>
              {!catalog.length ? (
                <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No matching dashboards with drawings.</div>
              ) : catalog.map((c) => (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 8px', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>{c.job_number} — {c.name}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{c.marker_count} markers · {c.drawing_mime || 'drawing'}</div>
                  </div>
                  <button
                    disabled={copying || String(c.job_number) === String(jobNumber)}
                    onClick={() => copyFrom(c.id, true)}
                    style={{ padding: '6px 10px', borderRadius: 8, border: 'none', background: '#0f766e', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 12, opacity: String(c.job_number) === String(jobNumber) ? 0.4 : 1 }}
                  >
                    Copy
                  </button>
                </div>
              ))}
            </div>
            <div style={{ padding: '10px 14px', fontSize: 11, color: '#64748b', borderTop: '1px solid #e2e8f0' }}>
              Copies drawing + markers. WBS and activity bindings stay as text — re-check them if this job uses different codes.
            </div>
          </div>
        </div>
      )}

      <GaDashboardHelpModal open={showHelp} onClose={() => setShowHelp(false)} />
    </div>
  );
};

export default GaDashboards;
